import React, { useEffect, useState } from "react";
import { Button, TextField, Paper, Grid, Snackbar, Alert, CircularProgress } from "@mui/material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const EmailConfiguration = () => {
  const axiosPrivate = useAxiosPrivate();
  const [form, setForm] = useState({
    MAIL_SERVER: "",
    MAIL_PORT: "",
    MAIL_USERNAME: "",
    MAIL_PASSWORD: "",
    MAIL_DEFAULT_SENDER: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [testing, setTesting] = useState(false);

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validateForm = () => {
    if (!form.MAIL_SERVER.trim()) return "Mail Server is required.";
    if (!form.MAIL_PORT || isNaN(Number(form.MAIL_PORT))) return "Mail Port must be a valid number.";
    if (!form.MAIL_USERNAME.trim()) return "Username (Email) is required.";
    if (!form.MAIL_PASSWORD.trim()) return "App Password is required.";
    return null;
  };

  const handleSave = async () => {
    const err = validateForm();
    if (err) { setSnackbar({ open: true, message: err, severity: "error" }); return; }
    try {
      await axiosPrivate.post("/api/auth/v1/email-config", { ...form, MAIL_PORT: Number(form.MAIL_PORT) });
      setSnackbar({ open: true, message: "Configuration saved successfully", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Failed to save configuration", severity: "error" });
    }
  };

  const handleTestConnection = async () => {
    const validationErr = validateForm();
    if (validationErr) { setSnackbar({ open: true, message: validationErr, severity: "error" }); return; }
    setTesting(true);
    try {
      const response = await axiosPrivate.post(
        "/api/auth/v1/email-config/test",
        { ...form, MAIL_PORT: Number(form.MAIL_PORT) },
      );
      setSnackbar({
        open: true,
        message: response.data.message || "Connection successful!",
        severity: "success",
      });
    } catch (err) {
      const raw = err.response?.data?.message;
      // API may return a validation object {MAIL_PORT: "..."} instead of a plain string
      const msg =
        typeof raw === "string"
          ? raw
          : raw && typeof raw === "object"
          ? Object.values(raw).join(", ")
          : "Connection failed. Please check your settings.";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setTesting(false);
    }
  };

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await axiosPrivate.get("/api/auth/v1/email-config");
        setForm((prev) => ({ ...prev, ...res.data }));
      } catch (err) {
        console.error(err);
      }
    };

    loadConfig();
  }, []);

  return (
    <>
      <div style={{ padding: "8px 4px" }}>
        <Paper
          elevation={0}
          sx={{
            border: "1px solid #e5e7eb",
            borderRadius: "14px",
            padding: "32px",
            background: "#fff",
            margin: "0 32px 0 0",
          }}
        >
          <div style={{ marginBottom: "28px" }}>
            <p
              style={{
                marginTop: "6px",
                fontSize: "14px",
                color: "#6b7280",
              }}
            >
              Configure SMTP settings used for system emails like password
              reset.
            </p>
          </div>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mail Server"
                placeholder="smtp.gmail.com"
                value={form.MAIL_SERVER}
                onChange={(e) => handleChange("MAIL_SERVER", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Mail Port"
                placeholder="587"
                value={form.MAIL_PORT}
                onChange={(e) => handleChange("MAIL_PORT", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Username (Email)"
                placeholder="admin@company.com"
                value={form.MAIL_USERNAME}
                onChange={(e) => handleChange("MAIL_USERNAME", e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="password"
                label="App Password"
                placeholder="••••••••"
                value={form.MAIL_PASSWORD}
                onChange={(e) => handleChange("MAIL_PASSWORD", e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Default Sender"
                placeholder="BrakePress <no-reply@company.com>"
                value={form.MAIL_DEFAULT_SENDER}
                onChange={(e) =>
                  handleChange("MAIL_DEFAULT_SENDER", e.target.value)
                }
              />
            </Grid>
          </Grid>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "32px",
              gap: "16px",
            }}
          >
            <Button
              variant="outlined"
              onClick={handleTestConnection}
              disabled={testing}
              startIcon={testing ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                paddingX: "26px",
                fontWeight: 600,
                color: "#49758d",
                borderColor: "#49758d",
                "&:hover": {
                  borderColor: "#3f667a",
                  backgroundColor: "rgba(73, 117, 141, 0.04)",
                },
              }}
            >
              {testing ? "Testing..." : "Test Connection"}
            </Button>
            <Button
              variant="contained"
              onClick={handleSave}
              sx={{
                backgroundColor: "#49758d",
                borderRadius: "10px",
                textTransform: "none",
                paddingX: "26px",
                fontWeight: 600,
                boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                "&:hover": {
                  backgroundColor: "#3f667a",
                },
              }}
            >
              Save Configuration
            </Button>
          </div>
        </Paper>
      </div>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: "10px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmailConfiguration;
